import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { apiConfig } from "../authConfig";
import { DOCX_PREVIEW_BODY_CLASS } from "./ArtefactTemplatePreview";
import { getOnboardingSampleArtefactHref } from "../constants/onboardingSampleArtefacts";

/**
 * Lead onboarding: educator sample .docx from `public/onboarding-sample-artefacts/` — fetch + authenticated mammoth preview.
 * @param {boolean} open
 * @param {{ id: string, fileNameWithExt: string, title?: string } | null} sample
 * @param {() => void} onClose
 * @param {() => Promise<string>} getApiToken
 */
export function OnboardingSampleDocxPreviewModal({ open, sample, onClose, getApiToken }) {
  const [status, setStatus] = useState("idle");
  const [html, setHtml] = useState("");

  useEffect(() => {
    if (!open || !sample?.fileNameWithExt) {
      setStatus("idle");
      setHtml("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setStatus("loading");
      setHtml("");
      try {
        const href = getOnboardingSampleArtefactHref(sample.fileNameWithExt);
        const blobRes = await fetch(href);
        if (!blobRes.ok) throw new Error("fetch failed");
        const blob = await blobRes.blob();
        const form = new FormData();
        form.append("file", blob, sample.fileNameWithExt);
        const token = await getApiToken();
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/docx-preview-html-upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        const h = json?.data?.html;
        if (!res.ok || !json.success || typeof h !== "string") {
          setStatus("error");
          return;
        }
        setHtml(h);
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sample?.id, sample?.fileNameWithExt, getApiToken]);

  const title = sample?.title
    ? `Sample: ${sample.title}`
    : sample?.fileNameWithExt || "Sample document";

  return (
    <Modal
      title={title}
      open={open && !!sample}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
      classNames={{ body: "max-h-[min(92vh,880px)]" }}
      styles={{ body: { overflow: "auto", maxHeight: "min(92vh, 880px)" } }}
    >
      {(status === "loading" || (open && sample && status === "idle")) && (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-sm font-medium text-[color:var(--color-text-muted)]">
          Loading preview…
        </div>
      )}
      {status === "error" && (
        <div className="min-h-[200px] rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-6 text-center text-xs text-[color:var(--color-warn-fg)]">
          Preview could not be loaded. Check that the API is reachable and you are signed in.
        </div>
      )}
      {status === "ready" && html && (
        <div
          className={`max-h-[min(88vh,820px)] overflow-auto rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-inner ${DOCX_PREVIEW_BODY_CLASS}`}
        >
          <div className="docx-preview" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      )}
    </Modal>
  );
}
